// src/pages/Maddalene.js
import React from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  useBreakpointValue,
  useColorModeValue,
  Container,
  VStack,
  List,
  ListItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import bgMaddalene from '../images/bg-maddalene.jpg';
import ContactForm from '../components/ContactForm';

// Giorni della settimana (Lun–Dom)
const WEEK_DAYS = [
  'Lunedì',
  'Martedì',
  'Mercoledì',
  'Giovedì',
  'Venerdì',
  'Sabato',
  'Domenica',
];

// Slot orari (9–24, ogni ora)
const TIME_SLOTS = [
  '09:00-10:00',
  '10:00-11:00',
  '11:00-12:00',
  '12:00-13:00',
  '13:00-14:00',
  '14:00-15:00',
  '15:00-16:00',
  '16:00-17:00',
  '17:00-18:00',
  '18:00-19:00',
  '19:00-20:00',
  '20:00-21:00',
  '21:00-22:00',
  '22:00-23:00',
  '23:00-24:00',
];

// Matrice orari: schedule[dayIndex][slotIndex] = nome attività o null
const buildEmptySchedule = () =>
  Array(WEEK_DAYS.length)
    .fill(null)
    .map(() => Array(TIME_SLOTS.length).fill(null));

const initialSchedule = buildEmptySchedule();

/** Imposta label per ogni slot da startHour (incluso) a endHour (escluso). */
const fillScheduleRange = (dayIdx, startHour, endHour, label) => {
  for (let hour = startHour; hour < endHour; hour += 1) {
    const slotIdx = hour - 9;
    if (slotIdx >= 0 && slotIdx < TIME_SLOTS.length) {
      initialSchedule[dayIdx][slotIdx] = label;
    }
  }
};

const ANZIANI = 'Associazione Anziani';
fillScheduleRange(1, 15, 24, ANZIANI); // Martedì 15:00–24:00
fillScheduleRange(2, 9, 12, ANZIANI); // Mercoledì 9:00–12:00
fillScheduleRange(3, 15, 18, ANZIANI); // Giovedì 15:00–18:00
fillScheduleRange(4, 19, 24, ANZIANI); // Venerdì 19:00–24:00

fillScheduleRange(4, 16, 19, 'Bro Out'); // Venerdì 16:00–19:00

const ACTIVITY_STYLES = {
  'Bro Out': {
    bg: 'rgba(54, 169, 225, 0.32)',
    accent: '#36a9e1',
  },
  'Associazione Anziani': {
    bg: 'rgba(62, 178, 151, 0.32)',
    accent: '#3eb297',
  },
};

const DEFAULT_ACTIVITY_STYLE = {
  bg: 'rgba(252, 186, 3, 0.22)',
  accent: '#fcba03',
};

const getActivityStyle = (label) =>
  ACTIVITY_STYLES[label] ?? DEFAULT_ACTIVITY_STYLE;

const SCHEDULE_ACTIVITY_LABELS = [
  ...new Set(initialSchedule.flat().filter(Boolean)),
].sort();

/** Celle da saltare (rowSpan) o da mostrare con span per una colonna giorno. */
const getDaySlotCell = (schedule, dayIdx, slotIdx) => {
  const label = schedule[dayIdx][slotIdx];
  if (label && slotIdx > 0 && schedule[dayIdx][slotIdx - 1] === label) {
    return { skip: true };
  }
  if (!label) {
    return { skip: false, rowSpan: 1, label: null };
  }
  let rowSpan = 1;
  while (
    slotIdx + rowSpan < TIME_SLOTS.length &&
    schedule[dayIdx][slotIdx + rowSpan] === label
  ) {
    rowSpan += 1;
  }
  return { skip: false, rowSpan, label };
};

/** Blocchi attività consecutivi per la vista mobile (fascia oraria unificata). */
const getMergedActivitiesForDay = (schedule, dayIdx) => {
  const blocks = [];
  let slotIdx = 0;
  while (slotIdx < TIME_SLOTS.length) {
    const label = schedule[dayIdx][slotIdx];
    if (label) {
      let span = 1;
      while (
        slotIdx + span < TIME_SLOTS.length &&
        schedule[dayIdx][slotIdx + span] === label
      ) {
        span += 1;
      }
      const [start] = TIME_SLOTS[slotIdx].split('-');
      const end = TIME_SLOTS[slotIdx + span - 1].split('-')[1];
      blocks.push({
        key: `${dayIdx}-${slotIdx}-${label}`,
        timeRange: `${start}-${end}`,
        label,
      });
      slotIdx += span;
    } else {
      slotIdx += 1;
    }
  }
  return blocks;
};

const Maddalene = () => {
  const headingSize = useBreakpointValue({ base: '3xl', md: '5xl', lg: '6xl' });
  const overlayColor = useColorModeValue('rgba(0,0,0,0.5)', 'rgba(0,0,0,0.7)');
  const showTableDesktop = useBreakpointValue({ base: false, md: true });

  return (
    <Box
      minH="100vh"
      position="relative"
      bg="black"
      color="white"
      overflow="hidden"
      pb={8}
    >
      {/* ========== HERO ========== */}
      <Box
        position="relative"
        minH={{ base: '50vh', md: '60vh' }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          zIndex={0}
          bgImage={`url(${bgMaddalene})`}
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            w: '100%',
            h: '100%',
            bg: overlayColor,
            zIndex: 1,
          }}
        />
        <Flex
          direction="column"
          align="center"
          justify="center"
          position="relative"
          zIndex={2}
          px={{ base: 4, md: 8 }}
          py={{ base: 10, md: 16 }}
        >
          <Heading
            as="h1"
            fontSize={headingSize}
            mb={3}
            textAlign="center"
            fontWeight="extrabold"
            letterSpacing="tight"
            textShadow="0 4px 32px #000, 0 1px 0 #fff"
            lineHeight="1.1"
          >
            La Casetta delle{' '}
            <Box as="span" color="#fcba03">
              Maddalene
            </Box>
          </Heading>
          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            textAlign="center"
            color="whiteAlpha.900"
            textShadow="0 1px 3px #000"
          >
            Uno spazio di incontro, creatività e comunità
          </Text>
        </Flex>
      </Box>

      {/* ========== INTRO ========== */}
      <Box
        mt={{ base: -4, md: -8 }}
        position="relative"
        zIndex={2}
        px={{ base: 2, md: 0 }}
      >
        <Box
          bg="#2f3136"
          py={{ base: 8, md: 12 }}
          px={{ base: 4, md: 6 }}
          mx={{ base: 4, md: 8 }}
          borderRadius="12px"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.600"
        >
          <Container maxW="container.lg">
            <VStack align="stretch" spacing={4}>
              <Heading as="h2" size="lg" color="white" fontWeight="extrabold">
                La Casetta delle Maddalene
              </Heading>
              <Text color="gray.300" lineHeight="1.7">
                La Casetta delle Maddalene è un progetto di CIL – Creatività in
                Libertà nato con l&apos;obiettivo di creare uno spazio
                accogliente dedicato all&apos;incontro, al gioco e alla
                creatività.
              </Text>
              <Text color="gray.300" lineHeight="1.7">
                L&apos;idea alla base del progetto è semplice ma potente:
                offrire un luogo dove bambini, ragazzi e famiglie possano
                incontrarsi, sperimentare attività creative e vivere momenti di
                socialità positiva. La casetta diventa così un punto di
                riferimento per il territorio, un piccolo laboratorio di
                comunità dove il gioco, la cultura e la creatività si
                incontrano.
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* ========== ORARI SETTIMANALI (SEZIONE PRINCIPALE) ========== */}
      <Box mt={6} position="relative" zIndex={2} px={{ base: 2, md: 0 }}>
        <Box
          bg="#36393f"
          py={{ base: 8, md: 12 }}
          px={{ base: 4, md: 6 }}
          mx={{ base: 4, md: 8 }}
          borderRadius="12px"
          boxShadow="lg"
          border="1px solid"
          borderColor="#fcba03"
          borderWidth="2px"
        >
          <Container maxW="container.xl">
            <Heading
              as="h2"
              size="xl"
              color="white"
              fontWeight="extrabold"
              mb={6}
              textAlign="center"
            >
              Attività settimanali
            </Heading>
            <Text
              color="gray.300"
              textAlign="center"
              mb={6}
              maxW="2xl"
              mx="auto"
            >
              Orari della Casetta delle Maddalene: scegli il giorno e l&apos;ora
              per trovare le attività in programma.
            </Text>

            {showTableDesktop ? (
              <TableContainer
                overflowX="auto"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.600"
              >
                <Table size="sm" variant="simple" colorScheme="whiteAlpha">
                  <Thead>
                    <Tr bg="gray.700">
                      <Th color="gray.200" w="100px" textAlign="center">
                        Ora
                      </Th>
                      {WEEK_DAYS.map((day) => (
                        <Th
                          key={day}
                          color="gray.200"
                          textAlign="center"
                          minW="90px"
                        >
                          {day}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {TIME_SLOTS.map((slot, slotIdx) => (
                      <Tr key={slot} _even={{ bg: 'whiteAlpha.50' }}>
                        <Td
                          color="gray.300"
                          fontWeight="medium"
                          textAlign="center"
                          whiteSpace="nowrap"
                        >
                          {slot}
                        </Td>
                        {WEEK_DAYS.map((dayName, dayIdx) => {
                          const cell = getDaySlotCell(
                            initialSchedule,
                            dayIdx,
                            slotIdx
                          );
                          if (cell.skip) return null;
                          const { label, rowSpan } = cell;
                          const activityStyle = label
                            ? getActivityStyle(label)
                            : null;
                          return (
                            <Td
                              key={`${dayName}-${slot}`}
                              rowSpan={rowSpan > 1 ? rowSpan : undefined}
                              textAlign="center"
                              verticalAlign="middle"
                              py={2}
                              minW="90px"
                              bg={activityStyle?.bg ?? 'transparent'}
                              color={label ? 'white' : 'gray.500'}
                              borderColor="gray.600"
                              borderWidth="1px"
                            >
                              {label || '–'}
                            </Td>
                          );
                        })}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <VStack align="stretch" spacing={4}>
                {WEEK_DAYS.map((day, dayIdx) => (
                  <Box
                    key={day}
                    bg="whiteAlpha.50"
                    borderRadius="md"
                    p={4}
                    border="1px solid"
                    borderColor="gray.600"
                  >
                    <Heading as="h3" size="sm" color="#fcba03" mb={3}>
                      {day}
                    </Heading>
                    <VStack align="stretch" spacing={2}>
                      {getMergedActivitiesForDay(initialSchedule, dayIdx).map(
                        ({ key, timeRange, label }) => {
                          const activityStyle = getActivityStyle(label);
                          return (
                            <Flex
                              key={key}
                              justify="space-between"
                              align="center"
                              bg={activityStyle.bg}
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Text fontSize="sm" color="gray.200">
                                {timeRange}
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="600"
                                color="white"
                              >
                                {label}
                              </Text>
                            </Flex>
                          );
                        }
                      )}
                      {getMergedActivitiesForDay(initialSchedule, dayIdx)
                        .length === 0 && (
                        <Text fontSize="sm" color="gray.500">
                          Nessuna attività in programma
                        </Text>
                      )}
                    </VStack>
                  </Box>
                ))}
              </VStack>
            )}

            {SCHEDULE_ACTIVITY_LABELS.length > 0 && (
              <Flex
                mt={6}
                gap={6}
                justify="center"
                flexWrap="wrap"
                role="list"
                aria-label="Legenda attività"
              >
                {SCHEDULE_ACTIVITY_LABELS.map((name) => {
                  const { accent } = getActivityStyle(name);
                  return (
                    <Flex key={name} align="center" gap={2} role="listitem">
                      <Box
                        w={3}
                        h={3}
                        borderRadius="sm"
                        bg={accent}
                        aria-hidden
                      />
                      <Text fontSize="sm" color="gray.300">
                        {name}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            )}
          </Container>
        </Box>
      </Box>

      {/* ========== UNO SPAZIO PER CRESCERE INSIEME ========== */}
      <Box mt={6} position="relative" zIndex={2} px={{ base: 2, md: 0 }}>
        <Box
          bg="#2f3136"
          py={{ base: 8, md: 12 }}
          px={{ base: 4, md: 6 }}
          mx={{ base: 4, md: 8 }}
          borderRadius="12px"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.600"
        >
          <Container maxW="container.lg">
            <VStack align="stretch" spacing={4}>
              <Heading as="h2" size="xl" color="white" fontWeight="extrabold">
                Uno spazio per crescere insieme
              </Heading>
              <Text color="gray.300" lineHeight="1.7">
                La Casetta delle Maddalene vuole essere molto più di una
                semplice sede per attività. È pensata come uno spazio vivo, in
                cui le persone possano sentirsi accolte e partecipare
                attivamente alla vita della comunità.
              </Text>
              <Text color="gray.300" lineHeight="1.7">
                Attraverso attività ludiche, educative e creative, il progetto
                mira a:
              </Text>
              <List spacing={2} color="gray.300">
                <ListItem display="flex" alignItems="flex-start" gap={3}>
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="#fcba03"
                    flexShrink={0}
                    mt={2}
                  />
                  favorire socialità e collaborazione
                </ListItem>
                <ListItem display="flex" alignItems="flex-start" gap={3}>
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="#fcba03"
                    flexShrink={0}
                    mt={2}
                  />
                  stimolare curiosità e creatività
                </ListItem>
                <ListItem display="flex" alignItems="flex-start" gap={3}>
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="#fcba03"
                    flexShrink={0}
                    mt={2}
                  />
                  offrire occasioni di apprendimento attraverso il gioco
                </ListItem>
                <ListItem display="flex" alignItems="flex-start" gap={3}>
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="#fcba03"
                    flexShrink={0}
                    mt={2}
                  />
                  creare un ambiente inclusivo e aperto a tutti
                </ListItem>
              </List>
              <Text color="gray.300" lineHeight="1.7">
                Il progetto nasce dalla convinzione che il gioco e la creatività
                siano strumenti fondamentali per la crescita personale e
                sociale.
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* ========== UN PROGETTO CHE CRESCE ========== */}
      <Box mt={6} position="relative" zIndex={2} px={{ base: 2, md: 0 }}>
        <Box
          bg="#2f3136"
          py={{ base: 8, md: 12 }}
          px={{ base: 4, md: 6 }}
          mx={{ base: 4, md: 8 }}
          borderRadius="12px"
          boxShadow="lg"
          border="1px solid"
          borderColor="gray.600"
        >
          <Container maxW="container.lg">
            <VStack align="stretch" spacing={4}>
              <Heading as="h2" size="xl" color="white" fontWeight="extrabold">
                Un progetto che cresce con la comunità
              </Heading>
              <Text color="gray.300" lineHeight="1.7">
                La Casetta delle Maddalene è un progetto in continua evoluzione.
                Come tutte le iniziative di CIL – Creatività in Libertà, cresce
                grazie alla partecipazione delle persone che la vivono: soci,
                volontari, educatori, famiglie e appassionati.
              </Text>
              <Text color="gray.300" lineHeight="1.7">
                Ogni attività è un&apos;occasione per costruire relazioni,
                condividere passioni e creare nuove opportunità di incontro.
              </Text>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* ========== VUOI PARTECIPARE? – Modulo Contattaci ========== */}
      <Box
        mt={6}
        position="relative"
        zIndex={2}
        px={{ base: 4, md: 6 }}
        mx={{ base: 4, md: 8 }}
      >
        <ContactForm
          title="Vuoi partecipare?"
          description="Se sei curioso di scoprire la Casetta delle Maddalene o vuoi partecipare alle attività, contattaci per avere maggiori informazioni. La porta della Casetta è sempre aperta a chi ha voglia di giocare, creare e costruire comunità insieme."
          titleSize="xl"
          variant="dark"
          maxW="100%"
        />
      </Box>
    </Box>
  );
};

export default Maddalene;
